import { useMemo, useState } from "react";

import clsx from "clsx";
import copy from "copy-to-clipboard";
import { isNil, orderBy } from "lodash";
import { useToasts } from "react-toast-notifications";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type DataTypeProvider } from "@devexpress/dx-react-grid";
import { type Dimensions } from "@features/sales-v3/charts/chart";
import { ViewInCreativeAnalyticsButton } from "@features/sales-v3/flyout/components/creatives/view-in-creative-analytics-button";
import { usePageConfiguration } from "@features/sales-v3/hooks";
import {
  useActiveTabState,
  type Atomicity,
  type FiltersState,
} from "@features/sales-v3/hooks/use-tab-state";
import { useSplitView } from "@features/sales-v3/hooks/use-table-selection";
import {
  getSelectAllCount,
  getSelectNoneMode,
  getSplitViewCount,
} from "@features/sales-v3/tabs/components/tab";
import { type SalesTableVisualRow } from "@features/sales-v3/types/table";
import { checkShouldDrawIconsInTable } from "@features/sales-v3/utils/table-state-helpers";
import {
  useUniversalBenchmarksRow,
  type BenchmarkIndicatorRow,
} from "@features/universal-benchmarks";
import { useFeatureFlags } from "@hooks/http";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { slugify } from "@north-beam/nb-common";
import { SecondaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

import { isParent } from "../table";

import { BenchmarksIndicator } from "./benchmarks-indicator";

function filterOutAtomChildren(
  filtersState: FiltersState,
  stringifiedAtom: string,
): FiltersState {
  return {
    ...filtersState,
    inclusionAtomicities: filtersState.inclusionAtomicities.filter(
      (potentialChild) => isParent(stringifiedAtom, potentialChild.atomicity),
    ),
    exclusionAtomicities: filtersState.exclusionAtomicities.filter(
      (potentialChild) => isParent(stringifiedAtom, potentialChild.atomicity),
    ),
  };
}

function findAtomParents(
  stringifiedAtom: string,
  potentialParents: Atomicity[],
) {
  return potentialParents.filter((potentialParent) =>
    isParent(potentialParent.atomicity, stringifiedAtom),
  );
}

function isSelectAll({
  stringifiedAtom,
  filtersState,
  defaultSelectAll,
}: {
  stringifiedAtom: string;
  filtersState: FiltersState;
  defaultSelectAll: boolean;
}) {
  // if we have already explcitly defined the state, just use the respective states

  if (
    filtersState.inclusionAtomicities.find(
      (i) => i.atomicity === stringifiedAtom,
    )
  ) {
    return true;
  }
  if (
    filtersState.exclusionAtomicities.find(
      (i) => i.atomicity === stringifiedAtom,
    )
  ) {
    return true;
  }

  // otherwise use your latest ancestor
  const parents = [
    ...findAtomParents(stringifiedAtom, filtersState.inclusionAtomicities).map(
      (atom) => ({ atom, side: "inclusion" }),
    ),
    ...findAtomParents(stringifiedAtom, filtersState.exclusionAtomicities).map(
      (atom) => ({ atom, side: "exclusiion" }),
    ),
  ];
  const deepestParents = orderBy(
    parents,
    (p) => JSON.parse(p.atom.atomicity).length,
    "desc",
  );

  if (!deepestParents.length) {
    return defaultSelectAll;
  }

  return deepestParents[0].side === "inclusion" ? true : false;
}

export const DimensionDataFormatter = ({
  row: _row,
  isCompacted,
  onCellClick,
}: DataTypeProvider.ValueFormatterProps & {
  isCompacted: boolean;
  onCellClick: (row: SalesTableVisualRow) => void;
}) => {
  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();
  const row = _row as SalesTableVisualRow;
  const { designElementManager } = useDesignElementManager();
  const rowMetadata = row.rowMetadata;
  const naming = rowMetadata?.naming;
  const { isBreakdownTab, tabState } = useActiveTabState();
  const {
    breakdowns,
    activeTab,
    filters,
    attributionModel,
    attributionWindow,
    accountingMode,
  } = usePageConfiguration();
  const prettyLogEventPage = usePrettyLogEventPage();

  const isSplitView = useSplitView();

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const currentSelected = useMemo(() => {
    if (!Array.isArray(JSON.parse(row.key))) {
      return 0;
    }

    const stringifiedAtom = row.key as string;
    const filtersState = filterOutAtomChildren(
      tabState.proposedFiltersState,
      stringifiedAtom,
    );

    if (isSplitView) {
      return getSplitViewCount({
        isBreakdownTab,
        filtersState,
      });
    }

    ////////// aggregate view mode //////////

    // need to find if it is selectAll mode or not
    const isSelectAllMode = isSelectAll({
      stringifiedAtom: row.key,
      filtersState: tabState.proposedFiltersState,
      defaultSelectAll: tabState.selectAll,
    });

    // default select ALL mode
    if (isSelectAllMode) {
      return getSelectAllCount({
        totalCount: row.rowMetadata.deepestAtomicityCounts,
        filtersState,
      });
    }

    // default select NONE mode
    const selectedCount = getSelectNoneMode({
      filtersState,
    });

    return selectedCount;
  }, [
    isBreakdownTab,
    isSplitView,
    row.key,
    row.rowMetadata.deepestAtomicityCounts,
    tabState.proposedFiltersState,
    tabState.selectAll,
  ]);

  const shouldDrawIcons = checkShouldDrawIconsInTable({
    breakdowns,
    activeTab,
  });
  const parsedRowKey = useMemo(() => JSON.parse(row.key), [row.key]);
  const iconKey = row.rowMetadata.platform || row.rowMetadata.naming.name;
  const icon = designElementManager.getDesignElement(iconKey).getComponent({
    size: "20px",
  });

  const currentDepth = breakdowns.findIndex(
    (b) => b.key === row.rowMetadata.breakdown,
  );
  const parsedKey = JSON.parse(row.key);
  const noBreakdowns = Array.isArray(parsedKey)
    ? (parsedKey as Dimensions[]).every((k) => !Object.keys(k).length) ||
      (breakdowns.length > 0 &&
        currentDepth !== Object.keys(row.rowMetadata.dimensions).length - 1)
    : false;

  const isAd = row.rowMetadata?.tab === "ad";
  const isAdset = row.rowMetadata?.tab === "adset";
  const isCampaign = row.rowMetadata?.tab === "campaign";

  const { benchmarkBreakdowns, benchmarkFilters } = useMemo(() => {
    let benchmarkFilters = [];

    for (const filter of filters) {
      for (const value of filter.values) {
        benchmarkFilters.push({ breakdown: filter.key, value });
      }
    }

    if (!row) {
      return { benchmarkBreakdowns: null, benchmarkFilters };
    }

    if (row.key === "default") {
      return { benchmarkBreakdowns: null, benchmarkFilters };
    }

    let benchmarkBreakdowns: Record<string, string>[] = [];

    if (breakdowns && breakdowns.length > 0) {
      for (const breakdown of breakdowns) {
        benchmarkBreakdowns.push({
          [breakdown.key]: row.rowMetadata.dimensions[breakdown.key],
        });
      }
    } else {
      benchmarkBreakdowns.push({
        "Platform (Northbeam)":
          row.rowMetadata.dimensions["Platform (Northbeam)"],
      });
    }

    let parsedRowKey;

    try {
      parsedRowKey = JSON.parse(row.key);
      // eslint-disable-next-line no-empty
    } catch {}

    if (Array.isArray(parsedRowKey)) {
      benchmarkBreakdowns = parsedRowKey;
    } else {
      benchmarkFilters = [];
    }

    return { benchmarkBreakdowns, benchmarkFilters };
  }, [row?.key, row?.rowMetadata, breakdowns, filters]);

  const universalBenchmarks = useUniversalBenchmarksRow({
    level:
      (rowMetadata?.tab === "breakdown" ? "campaign" : rowMetadata?.tab) ??
      "campaign",
    attributionModel,
    attributionWindow,
    accountingMode,
    breakdowns: benchmarkBreakdowns,
    filters: benchmarkFilters,
  });

  const benchmarkKpis = useMemo(() => {
    if (!row) {
      return [];
    }

    if (!universalBenchmarks) {
      return [];
    }

    const spendValue = row.export?.["spend"];

    if (!spendValue) {
      return [];
    }

    if (universalBenchmarks.benchmarks.every((e) => isNil(e.value))) {
      return [];
    }

    const rv: BenchmarkIndicatorRow[] = [];

    universalBenchmarks.benchmarks.forEach((bench) => {
      const metric = bench.metric;

      if (!metric?.metadata.salesTableMetricId) {
        return;
      }

      rv.push({
        metricId: metric.metadata.salesTableMetricId,
        name:
          metric.name ??
          metric.metadata.name ??
          metric.spec?.name ??
          metric.metadata.salesTableMetricId,
        current: row.export?.[metric.metadata.salesTableMetricId],
        target: bench.value,
        format: metric.format,
        positiveTrend: metric.isPositiveChangeGood ? "up" : "down",
      });
    });

    return rv;
  }, [row, user.currencies.currentCurrency, universalBenchmarks]);

  if (rowMetadata?.tab === "breakdown" || Array.isArray(parsedRowKey)) {
    return (
      <div className="flex flex-col justify-center h-full relative name-table-cell">
        <HoverStateOptions isCompact={isCompacted} row={row} withoutFlyout />
        <div className="flex items-center space-x-2 font-semibold font-sans truncate">
          {shouldDrawIcons && icon}
          <div className={clsx("truncate", { "leading-4": isCompacted })}>
            {row.rowMetadata.breakdown && (
              <div
                className={clsx(
                  "font-sans text-[0.5rem] text-gray+2 leading-3 uppercase truncate",
                  { "mb-1": !isCompacted },
                )}
              >
                {row.rowMetadata.breakdown.toUpperCase()}{" "}
                {row.hasChildren && !noBreakdowns
                  ? `(${currentSelected} out of ${row.rowMetadata.deepestAtomicityCounts})`
                  : ""}
              </div>
            )}
            <span
              className={clsx("text-primary", {
                "cursor-pointer hover:text-cosmic+1": !isAd,
              })}
              onClick={() => onCellClick(row)}
            >
              {naming?.name}
            </span>
          </div>
          <BenchmarksIndicator
            benchmarkItemName={naming?.name}
            universalBenchmarks={universalBenchmarks}
            benchmarkIndicatorRows={benchmarkKpis}
          />
        </div>
      </div>
    );
  }

  let sourceAndMedium = null;
  if (isCampaign) {
    try {
      const adKey = JSON.parse(row.key);
      sourceAndMedium = JSON.parse(adKey?.platform);
    } catch {
      // Do nothing
    }
  }

  return (
    <div className="flex items-center space-x-2 h-full font-sans relative name-table-cell">
      {icon}
      <HoverStateOptions isCompact={isCompacted} row={row} />
      {hasFeatureFlag("enableThumbnailsOnSalesPage") && isAd && (
        <>
          {rowMetadata.imageUri ? (
            <>
              <div
                className={clsx(
                  "border border-gray rounded bg-white cursor-pointer hover:border-cosmic+1",
                )}
                onClick={(ev) => {
                  if (!rowMetadata.imageUri) {
                    return;
                  }

                  ev.stopPropagation();

                  prettyLogEventPage({
                    domain: "Dimension Data Formatter",
                    action: "onClick",
                    additionalActionInfo: "Open Thumbnail Preview Modal",
                  });

                  setIsPreviewModalOpen(true);
                }}
              >
                <img
                  className={clsx("object-scale-down rounded", {
                    "w-[30px]": isCompacted,
                    "w-[40px]": !isCompacted,
                  })}
                  src={rowMetadata.imageUri}
                  alt={`${naming.name} ad`}
                />
              </div>
              <ThumbnailPreviewModal
                isOpen={isPreviewModalOpen}
                onClose={() => setIsPreviewModalOpen(false)}
                row={row}
              />
            </>
          ) : (
            <div
              className={clsx("text-xxxs flex items-center justify-center", {
                "w-[26px] h-[26px]": isCompacted,
                "w-[35px] h-[35px]": !isCompacted,
              })}
            >
              <i
                className={clsx("fa-regular fa-image", {
                  "text-[26px]": isCompacted,
                  "text-[35px]": !isCompacted,
                })}
              />
            </div>
          )}
        </>
      )}
      <div className="flex items-start flex-column w-full truncate">
        <div
          className={clsx(
            "font-semibold text-xs text-left text-cosmic w-full truncate",
            {
              "leading-3": isAd,
              "cursor-pointer hover:text-cosmic+1": !isAd,
            },
          )}
          onClick={() => !isAd && onCellClick(row)}
        >
          <div className="flex items-center gap-2 truncate">
            <div className="truncate">{naming.name}</div>
            <BenchmarksIndicator
              benchmarkItemName={naming.name}
              universalBenchmarks={universalBenchmarks}
              benchmarkIndicatorRows={benchmarkKpis}
            />
          </div>
        </div>
        {isCampaign && sourceAndMedium?.medium && (
          <span className="cell-subtitle font-normal w-full truncate leading-3">
            Medium: {sourceAndMedium.medium}
          </span>
        )}
        {isCampaign && sourceAndMedium?.source && (
          <span className="cell-subtitle font-normal w-full truncate leading-3">
            Source: {sourceAndMedium.source}
          </span>
        )}
        {isAd && (
          <span className="cell-subtitle font-normal w-full truncate leading-3">
            {naming.adsetName}
          </span>
        )}
        {(isAdset || isAd) && (
          <span className="cell-subtitle font-normal w-full truncate leading-3">
            {naming.campaignName}
          </span>
        )}
      </div>
    </div>
  );
};

function HoverStateOptions({
  row,
  withoutFlyout,
}: {
  isCompact: boolean;
  row: SalesTableVisualRow;
  withoutFlyout?: boolean;
}) {
  const rowMetadata = row.rowMetadata;
  const naming = rowMetadata?.naming;
  const { addToast } = useToasts();
  const prettyLogEventPage = usePrettyLogEventPage();
  const { setFlyoutId } = usePageConfiguration();
  return (
    <div
      className="hide-cell-actions absolute h-full right-0"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="h-full flex items-center space-x-2">
        {!withoutFlyout && (
          <GenericTooltip
            content="Open flyout"
            noInfoCircle
            overlayClassName="pointer-events-none"
          >
            <span
              className={clsx(
                "bg-white elevation-1 rounded-full cursor-pointer text-primary",
                "font-bold py-0.5 px-2.5 text-xs flex items-center",
              )}
              onClick={() => {
                const parsedKey = JSON.parse((row as any).key);
                const isBreakdown = Array.isArray(parsedKey);
                if (isBreakdown) {
                  return;
                }

                prettyLogEventPage({
                  domain: "Flyout",
                  action: "onClick",
                  additionalActionInfo: "Open Flyout",
                  data: {
                    pageContext: { parsedKey },
                  },
                });

                setFlyoutId(slugify(parsedKey));
              }}
            >
              <i
                className={clsx(
                  "fa-sharp fa-solid fa-sidebar-flip font-light text-sm mr-1",
                )}
              ></i>
              <span>OPEN</span>
            </span>
          </GenericTooltip>
        )}
        <GenericTooltip
          content="Copy"
          noInfoCircle
          overlayClassName="pointer-events-none"
        >
          <i
            className={clsx(
              "bg-white elevation-1 rounded-full p-[5px] fa-sharp fa-solid fa-clone",
              "cursor-pointer text-md text-primary font-light",
            )}
            onClick={() => {
              addToast("COPIED NAME TO CLIPBOARD", { appearance: "success" });
              copy(naming?.name);
            }}
          ></i>
        </GenericTooltip>
      </div>
    </div>
  );
}

function ThumbnailPreviewModal({
  isOpen,
  row,
  onClose,
}: {
  isOpen: boolean;
  row: SalesTableVisualRow;
  onClose: () => void;
}) {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { setFlyoutId, setFlyoutTab } = usePageConfiguration();

  const title = row.rowMetadata.naming.name;

  if (!row.rowMetadata.imageUri) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      headerTitleProps={{ className: "truncate" }}
      titleContent={
        <GenericTooltip noInfoCircle content={title}>
          <div className="font-bold truncate mr-8">{title}</div>
        </GenericTooltip>
      }
    >
      <div className="border border-gray">
        <img
          className="object-scale-down"
          src={row.rowMetadata.imageUri}
          alt={`${title} ad`}
        />
      </div>
      <div className="flex items-center justify-end gap-2 mt-4">
        <SecondaryButton
          className="font-bold flex items-center gap-2"
          onClick={() => {
            const parsedKey = JSON.parse((row as any).key);
            const isBreakdown = Array.isArray(parsedKey);
            if (isBreakdown) {
              return;
            }

            prettyLogEventPage({
              domain: "Flyout",
              action: "onClick",
              additionalActionInfo: "Open Flyout",
              data: {
                pageContext: { parsedKey },
              },
            });

            onClose();
            setFlyoutId(slugify(parsedKey));
            setFlyoutTab("creatives");
          }}
        >
          <i className="fa-sharp fa-solid fa-sidebar-flip font-light"></i>
          OPEN FLYOUT
        </SecondaryButton>
        <ViewInCreativeAnalyticsButton
          className="button primary-button sm inline-block no-underline font-bold py-[7px]"
          query={title}
          icon={<i className="fa-regular fa-image text-white" />}
        />
      </div>
    </Modal>
  );
}
